import React, { useState } from 'react';
import { Navigate } from 'react-router-dom'
import Grid from '../components/Grid'
import {
    IconButton, Button, createTheme, ThemeProvider,
    Dialog, DialogContent, DialogContentText, DialogTitle, DialogActions,
    FormControl, Select, MenuItem
} from '@mui/material/';
import SettingsIcon from '@mui/icons-material/Settings';
import CachedIcon from '@mui/icons-material/Cached';
import StopCircleIcon from '@mui/icons-material/StopCircle';

import { newBoard, decodeBoard, performFlip } from '../util/boardUtils'
import { squishArrayIntoColumns, getSquishRows } from '../util/utils'
import { solveTimer, roundOverPopup, settingsPopup } from '../components/mini/Gamelobby_mini'

import './Gamelobby.css';
import PlayerGrid from '../components/PlayerGrid';

function Gamelobby({ socket, identity, setIdentity, theme, setTheme }) {

    const [settingsOpen, setSettingsOpen] = useState(false);
    const [lobbySettingsOpen, setLobbySettingsOpen] = useState(false);
    const [roundboxOpen, setRoundboxOpen] = useState(false);
    const [settingsDifficulty, setSettingsDifficulty] = useState(7)
    const [settingsSize, setSettingsSize] = useState(5)

    const handleSettingsOpen = () => { setSettingsOpen(true); };
    const handleRoundboxOpen = () => { setRoundboxOpen(true); };
    const handleSettingsClose = () => { setSettingsOpen(false); };
    const handleRoundboxClose = () => { setRoundboxOpen(false); };

    const handleSettingsSave = () => {
        console.log("Change our name to " + document.getElementById("settingsName").value)
        socket.emit('updateName', document.getElementById("settingsName").value)
        setSettingsOpen(false);
    };

    const handleLobbySettingsSave = () => {
        const msg = { difficulty: settingsDifficulty, size: settingsSize }
        socket.emit('updateSettings', JSON.stringify(msg))
        console.log('UPdate settings!"')
        setLobbySettingsOpen(false)
    }

    const [gameObjects, setGameObjects] = useState({
        curPlayer: { id: null, name: null, solveTime: null, board: newBoard(5), score: 0, scoreGain: 0 },
        otherPlayers: [], targetBoard: newBoard(5), startTime: null, host: null, roundOver: true,
        size: 5, difficulty: 7
    })
    const [ignoreBoardUpdatesUntil, setIgnoreBoardUpdatesUntil] = useState(0)
    const [needLeaveRoom, setNeedLeaveRoom] = useState(false)

    if (socket) {
        socket.off('gameObjectUpdate')
        socket.on('gameObjectUpdate', (msg) => {
            const curTime = +new Date()
            const newGameObjects = JSON.parse(msg)
            if (curTime < ignoreBoardUpdatesUntil) newGameObjects.curPlayer.board = gameObjects.curPlayer.board
            else newGameObjects.curPlayer.board = decodeBoard(newGameObjects.curPlayer.board)

            newGameObjects.targetBoard = decodeBoard(newGameObjects.targetBoard)

            for (var i = 0; i < newGameObjects.otherPlayers.length; i++) {
                newGameObjects.otherPlayers[i].board = decodeBoard(newGameObjects.otherPlayers[i].board)
            }

            if (!gameObjects.roundOver && newGameObjects.roundOver) {
                handleRoundboxOpen()
            }
            setGameObjects(newGameObjects)
        })
    }

    if (needLeaveRoom) {
        socket.off('gameObjectUpdate')
        return <Navigate to={'/'} />
    }

    if (identity.id == null || identity.room == null) {
        // If we're here, we should have gotten here by direct-joining the link.
        // In this case, we're a guest assuming there's a room like this.
        console.log("Problem with our identity...")
        const url = window.location.href
        const expCode = url.match("/[^/]*$")[0].slice(1)
        if (socket != null) {
            console.log("Attempting to join " + expCode)
            socket.emit("attemptGuestJoin", JSON.stringify({ room: expCode }))
            socket.on('identify', (msg) => {
                socket.off('identify')
                socket.off('failedGuestJoin')
                console.log("Rec'd a new identity: " + msg)
                setIdentity(JSON.parse(msg))
            })
            socket.on('failedGuestJoin', (msg) => {
                console.log("Failed to login to " + expCode + " as a guest, redirecting to homepage.")
                socket.off('identify')
                socket.off('failedGuestJoin')
                setNeedLeaveRoom(true)
            })
        }

        return (
            <div>
                <p>Connecting...</p>
            </div>
        )
    }


    const muiDarkTheme = createTheme({
        palette: {
            primary: { main: '#808080' },
            secondary: { main: '#4169e1' },
            background: { paper: '#303030' },
            text: {
                primary: "#4169e1",
                secondary: "#800080",
            }
        },
    });

    const muiLightTheme = createTheme({
        palette: {
            primary: { main: '#4169e1' },
            secondary: { main: '#808080' },
            background: { paper: '#ffffff' },
            text: {
                primary: "#000000",
                secondary: "#4169e1",
            }
        },
    });

    var borderWidth = 3
    var boardSize = 300
    var subBoardSize = 200

    const squishedPlayers = squishArrayIntoColumns(gameObjects.otherPlayers, getSquishRows(gameObjects.otherPlayers.length))

    const styles = {
        gridContainer: {
            display: 'flex',
            flexDirection: 'row',
            flexWrap: 'wrap',
            minWidth: boardSize,
            width: boardSize,
            backgroundColor: 'cyan',
            borderWidth: borderWidth,
            borderStyle: 'solid',
            borderColor: 'black',
        },
        playerGridContainer: {
            display: 'flex',
            flexDirection: 'row',
            flexWrap: 'wrap',
            minWidth: (subBoardSize) * squishedPlayers[0].length,
            width: (subBoardSize + 20) * (squishedPlayers[0].length + 0.1),
            gap: 10
        },
    }

    // Update a cell to the cyclic next state
    const cellClicked = (y, x) => {
        console.log("Click!")
        performFlip(y, x, gameObjects.curPlayer.board)
        socket.emit('boardMove', JSON.stringify({ r: y, c: x }))
        setIgnoreBoardUpdatesUntil(+new Date() + 300)
    }

    const handleBoardRefresh = () => { socket.emit('refreshTargetBoard', JSON.stringify({ difficulty: 7 })) }
    const handleForceStop = () => { socket.emit('forceStopRound') }

    const toggleTheme = () => {
        if (theme === 'dark') setTheme('light')
        else setTheme('dark')
    }

    return (
        <div className="App">
            {socket ? (<div>
                <ThemeProvider theme={theme === 'dark' ? muiDarkTheme : muiLightTheme}>
                    <IconButton size="large" onClick={handleSettingsOpen}><SettingsIcon sx={{ color: 'var(--text-primary)' }} /></IconButton>
                    {window.location.href.includes('localhost') ?
                        <div>
                            <IconButton size="large" onClick={handleRoundboxOpen}><SettingsIcon sx={{ color: 'var(--negative)' }} /></IconButton>
                            <IconButton size="large" onClick={()=>{console.log(gameObjects)}}><SettingsIcon sx={{ color: 'var(--positive)' }} /></IconButton>
                        </div>
                        : <></>}
                    {settingsPopup(settingsOpen, handleSettingsClose, gameObjects, theme, toggleTheme, handleSettingsSave)}
                    {roundOverPopup(gameObjects, roundboxOpen, handleRoundboxClose)}
                    <Dialog open={lobbySettingsOpen} onClose={() => { setLobbySettingsOpen(false); setSettingsDifficulty(gameObjects.difficulty) }}>
                        <DialogTitle>Lobby Settings</DialogTitle>
                        <DialogContent>
                            <div className="rowflex">
                                <DialogContentText>Difficulty: </DialogContentText>
                                <FormControl sx={{ m: 1 }}>
                                    <Select
                                        value={settingsDifficulty}
                                        onChange={(event) => { setSettingsDifficulty(event.target.value) }}
                                    >
                                        <MenuItem value={3}>3</MenuItem>
                                        <MenuItem value={4}>4</MenuItem>
                                        <MenuItem value={5}>5</MenuItem>
                                        <MenuItem value={6}>6</MenuItem>
                                        <MenuItem value={7}>7</MenuItem>
                                        <MenuItem value={8}>8</MenuItem>
                                        <MenuItem value={9}>9</MenuItem>
                                        <MenuItem value={10}>10</MenuItem>
                                    </Select>
                                </FormControl>
                            </div>
                            <div className="rowflex">
                                <DialogContentText>Board Size: </DialogContentText>
                                <FormControl sx={{ m: 1 }}>
                                    <Select
                                        value={settingsSize}
                                        onChange={(event) => { setSettingsSize(event.target.value) }}
                                    >
                                        <MenuItem value={5}>5</MenuItem>
                                        <MenuItem value={6}>6</MenuItem>
                                        <MenuItem value={7}>7</MenuItem>
                                        <MenuItem value={8}>8</MenuItem>
                                        <MenuItem value={9}>9</MenuItem>
                                    </Select>
                                </FormControl>
                            </div>
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={handleLobbySettingsSave}>Save</Button>
                        </DialogActions>
                    </Dialog>
                    <div className="game-container">
                        <div className='rowflex' style={{ gap: 100 }}>
                            <div className='content'>
                                {gameObjects.host && (gameObjects.host === gameObjects.curPlayer.id) ?
                                    <div>
                                        {
                                            gameObjects.roundOver ?
                                                <IconButton size="large" onClick={handleBoardRefresh}><CachedIcon sx={{ color: 'var(--positive)' }} /></IconButton>
                                                : <IconButton size="large" onClick={handleForceStop}><StopCircleIcon sx={{ color: 'var(--negative)' }} /></IconButton>
                                        }
                                        <Button variant="contained" size="large" onClick={() => { setLobbySettingsOpen(true) }}>Lobby Settings</Button>
                                    </div>
                                    : <></>}
                                <Grid
                                    boardState={gameObjects.targetBoard}
                                    style={styles.gridContainer}
                                    boardSize={boardSize}
                                    cellClicked={() => { }}
                                    isClickable={false}
                                />
                                {solveTimer(gameObjects)}
                                <Grid boardState={gameObjects.curPlayer.board}
                                    style={styles.gridContainer}
                                    boardSize={boardSize}
                                    cellClicked={cellClicked}
                                    isClickable={true}
                                />
                            </div>
                            <PlayerGrid
                                style={styles.playerGridContainer}
                                boardSize={subBoardSize}
                                players={squishedPlayers}
                            />
                        </div>
                    </div>
                </ThemeProvider>
            </div>
            ) : (<div>Not Connected</div>)
            }
        </div >
    );
}

export default Gamelobby;